import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { PaperIconComponent } from '@ricoh-paper/angular';
import { ChromeService } from './chrome.service';

/**
 * The breadcrumb slot — two states of one slot
 * (docs/design/DESIGN_GUIDELINES.md § Breadcrumb slot):
 *
 *   • List page  — a plain label (12.5px/600), not a link. One word for where
 *     you are.
 *   • Detail page — the record chain:
 *       [parent list ›] [pill: mark · name · ⌄ · | · ×] [n of N]
 *
 * Shrink order, tightest last to give way: the pager gives way first, then the
 * parent crumb down to its 6ch floor, and the record pill LAST — it carries
 * min-width:150px so the record's own name is never what collapses. The whole
 * chain is one `flex: 0 1 auto; min-width: 0; overflow: hidden` group so the
 * utilities never shrink; the chain absorbs the squeeze.
 */
@Component({
  selector: 'ws-record-chain',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, PaperIconComponent],
  template: `
    @if (chrome.record(); as rec) {
      <span class="chain">
        <a class="crumb" [routerLink]="rec.parentRoute">{{ rec.parentLabel }}</a>
        <span class="sep" aria-hidden="true"><paper-icon name="chevron-right" [size]="13" /></span>

        <span class="pill">
          <!-- A record that carries a logo is recognised by it. The tinted
               initials tile stays the fallback for the ones that do not. -->
          @if (rec.logo) {
            <img class="mark mark--logo" [src]="rec.logo" alt="" aria-hidden="true" />
          } @else {
            <span class="mark" aria-hidden="true" [style.background]="rec.colour">{{ rec.initials }}</span>
          }
          <span class="pill-name">{{ rec.name }}</span>
          <button
            type="button"
            class="ctrl switch"
            aria-label="Switch record"
            (click)="switch()"
          >
            <paper-icon name="chevron-down" [size]="12" />
          </button>
          <span class="pill-div" aria-hidden="true"></span>
          <a class="ctrl leave" [routerLink]="rec.parentRoute" [attr.aria-label]="'Leave ' + rec.name" (click)="chrome.exitRecord()">
            <paper-icon name="close" [size]="12" />
          </a>
        </span>

        @if (rec.index && rec.total) {
          <span class="pager">{{ rec.index }} of {{ rec.total }}</span>
        }
      </span>
    } @else {
      <span class="list-label">{{ chrome.listLabel() || fallbackLabel() }}</span>
    }
  `,
  styles: [
    `
      :host {
        display: flex;
        align-items: center;
        min-width: 0;
        flex: 0 1 auto;
        overflow: hidden;
      }
      .list-label {
        font-size: 12.5px;
        font-weight: 600;
        color: var(--text);
        white-space: nowrap;
      }
      /* Whole chain absorbs the squeeze; utilities never shrink. */
      .chain {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
      }
      .crumb {
        flex: 0 1 auto;
        min-width: 6ch;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding: 5px 8px;
        font-size: 12.5px;
        font-weight: 500;
        line-height: 1.3;
        color: var(--text-secondary);
        border-radius: var(--radius-pill);
      }
      .crumb:hover {
        color: var(--text);
        background: var(--surface-hover);
      }
      .sep {
        display: inline-flex;
        flex: 0 0 auto;
        color: var(--text-tertiary);
      }
      /* Record pill — last to give way; the name never collapses below the pill floor. */
      .pill {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        flex: 0 1 auto;
        min-width: 150px;
        overflow: hidden;
        padding: 4px 5px 4px 6px;
        border-radius: var(--radius-pill);
        background: var(--surface-sunken);
        border: 1px solid var(--border-subtle);
        white-space: nowrap;
      }
      .mark {
        display: inline-grid;
        place-items: center;
        flex: 0 0 auto;
        width: 20px;
        height: 20px;
        border-radius: var(--radius-sm);
        color: #ffffff;
        font-size: 8.5px;
        font-weight: 600;
      }
      /* object-fit contain, not cover: cropping a wordmark to a 20px square is
         what makes a recognisable logo unrecognisable. */
      .mark--logo {
        object-fit: contain;
        background: var(--surface-2);
        box-shadow: inset 0 0 0 1px var(--border-subtle);
      }
      .pill-name {
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--text);
      }
      .ctrl {
        display: inline-grid;
        place-items: center;
        flex: 0 0 auto;
        width: 18px;
        height: 18px;
        border: none;
        border-radius: 50%;
        background: transparent;
        color: var(--text-tertiary);
        cursor: pointer;
      }
      .ctrl.leave {
        width: 19px;
        height: 19px;
      }
      .ctrl:hover {
        color: var(--text);
        background: var(--surface-hover);
      }
      .pill-div {
        width: 1px;
        height: 14px;
        flex: 0 0 auto;
        background: var(--border-subtle);
      }
      /* Pager: numerals only, gives way first. No stepper arrows (they were
         always crushed below their min-content width). */
      .pager {
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
        margin-left: 2px;
        font-size: 11.5px;
        color: var(--text-secondary);
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
    `,
  ],
})
export class RecordChainComponent {
  protected readonly chrome = inject(ChromeService);

  /** Fallback list label when a page has not set one — the active rail item. */
  readonly fallbackLabel = input<string>('');

  /** Phase 2 stub: the searchable record switcher lands with the record data in
   *  Phase 3. Kept as a present, focusable control so the chain's structure is
   *  complete and testable. */
  protected switch(): void {
    /* no-op until the record switcher is wired in Phase 3 */
  }
}
